import {useState, useEffect} from "react";
import {bookSlot} from "../../services/sheduleApi";
import {getCandidate, getCandidates, sendInterviewInvite} from "../../services/candidateApi";
import InterviewInviteModal from "../candidates/InterviewInviteModal";
import InterviewReminderModal from "./InterviewReminderModal";
import {useAuth} from "../../context/AuthContext";
import {useAlertContext} from "../../context/AlertContext";
import { formatDateRu } from "../../utils/dateFormat";
import { HR_INTERVIEW_REMINDER_QUESTION } from "../../utils/interviewReminder";
import "./CalendarDialogs.css";

export default function BookingModal({slot, onClose, onSuccess, onDeleteSlot}) {
    const [candidates, setCandidates] = useState([]);
    const [selectedCandidateId, setSelectedCandidateId] = useState("");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadingCandidates, setLoadingCandidates] = useState(true);
    const [inviteCandidate, setInviteCandidate] = useState(null);
    const [reminderCandidate, setReminderCandidate] = useState(null);
    const {showAlert} = useAlertContext();
    const {user} = useAuth() || {};
    const selectedCandidate = candidates.find((candidate) => String(candidate.id) === String(selectedCandidateId));

    useEffect(() => {
        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                setLoadingCandidates(true);
                // One page only — avoid flooding API (was up to 20×100 requests)
                const data = await getCandidates(
                    null,
                    search.trim() || null,
                    null,
                    null,
                    undefined,
                    0,
                    50
                );
                if (cancelled) return;
                const items = Array.isArray(data) ? data : (data?.items ?? []);
                setCandidates(items);
            } catch (e) {
                if (!cancelled) {
                    showAlert(`Ошибка загрузки кандидатов: ${e.message || e}`, "error");
                    setCandidates([]);
                }
            } finally {
                if (!cancelled) setLoadingCandidates(false);
            }
        }, search ? 300 : 0);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [search, showAlert]);

    const askHrReminder = (candidate) => {
        const wantReminder = window.confirm(HR_INTERVIEW_REMINDER_QUESTION);
        if (wantReminder && candidate?.id) {
            setReminderCandidate(candidate);
            return;
        }
        onSuccess();
        onClose();
    };

    const handleBook = async () => {
        try {
            setLoading(true);
            const candidateIdNum = Number(selectedCandidateId);
            await bookSlot(slot.id, candidateIdNum);
            showAlert("Слот успешно забронирован!", "success");
            const bookedCandidate = {
                ...(candidates.find((c) => String(c.id) === String(candidateIdNum)) || { id: candidateIdNum }),
            };
            try {
                const fresh = await getCandidate(candidateIdNum);
                if (fresh && typeof fresh === "object") {
                    Object.assign(bookedCandidate, fresh);
                }
            } catch (_) {
                /* keep list row */
            }
            const wantHhMessage = window.confirm(
                "Отправить кандидату сообщение о собеседовании на hh.ru?"
            );
            if (wantHhMessage) {
                setInviteCandidate(bookedCandidate);
                return;
            }
            askHrReminder(bookedCandidate);
        } catch (e) {
            showAlert(`Ошибка бронирования: ${e.message}`, "error");
        } finally {
            setLoading(false);
        }
    };

    const afterInviteFlow = () => {
        const source = inviteCandidate
            || candidates.find((c) => String(c.id) === String(selectedCandidateId))
            || { id: Number(selectedCandidateId) };
        setInviteCandidate(null);
        askHrReminder(source);
    };

    const submitInterviewInvite = async (message, _schedule, reminder) => {
        const candidateIdNum = Number(inviteCandidate?.id || selectedCandidateId);
        try {
            const resp = await sendInterviewInvite(candidateIdNum, {
                message,
                hr_id: user?.erp_user_id || user?.id || null,
                interview_date: slot.date,
                start_time: String(slot.start_time || "").slice(0, 5),
                end_time: String(slot.end_time || "").slice(0, 5),
                book_calendar: false,
                remind_candidate: Boolean(reminder?.enabled),
                remind_at: reminder?.remind_at || null,
                reminder_message: reminder?.message || null,
            });
            if (resp?.delivery && resp.delivery !== "sent" && resp.delivery !== "ok") {
                showAlert(
                    resp?.warning || "Собеседование сохранено, но HH пока не принял приглашение.",
                    "warning"
                );
            } else {
                showAlert("Приглашение на собеседование отправлено кандидату в HH", "success");
            }
            afterInviteFlow();
        } catch (e) {
            showAlert(`Ошибка отправки приглашения: ${e?.message || e}`, "error");
            throw e;
        }
    };

    const closeAfterReminder = (refresh) => {
        setReminderCandidate(null);
        if (refresh) onSuccess();
        onClose();
    };

    return (
        <>
            {!reminderCandidate ? (
                <div className="calendar-dialog-backdrop">
                    <div className="calendar-dialog calendar-booking-dialog" role="dialog" aria-modal="true" aria-labelledby="booking-slot-title">
                        <div className="calendar-dialog-header">
                            <div>
                                <span className="calendar-dialog-kicker">БРОНИРОВАНИЕ ВСТРЕЧИ</span>
                                <h3 id="booking-slot-title">Выберите кандидата</h3>
                                <p>Закрепите свободное время за кандидатом и настройте приглашение.</p>
                            </div>
                            <button type="button" className="calendar-dialog-close" onClick={onClose} disabled={loading} aria-label="Закрыть">×</button>
                        </div>
                        <div className="calendar-dialog-body">
                            <div className="calendar-slot-summary mb-5">
                                <div><span>Дата</span><strong>{formatDateRu(slot.date)}</strong></div>
                                <div><span>Время</span><strong>{String(slot.start_time || "").slice(0, 5)}–{String(slot.end_time || "").slice(0, 5)}</strong></div>
                            </div>

                            <label className="block mb-4">
                                <span className="calendar-field-label">Поиск кандидата</span>
                            <input
                                    className="calendar-field"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Введите имя или фамилию"
                                    data-testid="booking-candidate-search"
                                />
                                <span className="calendar-field-hint">Показано не более 50 наиболее подходящих кандидатов</span>
                            </label>
                            <label className="block">
                                <span className="calendar-field-label">Кандидат <b>*</b></span>
                            {loadingCandidates ? (
                                <div className="calendar-field calendar-field--loading">Загружаем кандидатов…</div>
                            ) : (
                                <select
                                    value={selectedCandidateId}
                                    onChange={e => setSelectedCandidateId(e.target.value)}
                                    className="calendar-field"
                                    data-testid="booking-candidate-select"
                                >
                                    <option value="">Выберите кандидата</option>
                                    {candidates.map(c => (
                                        <option key={c.id} value={c.id}>{c.full_name || `Кандидат #${c.id}`}</option>
                                    ))}
                                </select>
                            )}
                            </label>
                            {selectedCandidate && (
                                <div className="calendar-candidate-preview">
                                    <span className="calendar-candidate-avatar">{String(selectedCandidate.full_name || "К").split(/\s+/).slice(0, 2).map((part) => part[0]).join("")}</span>
                                    <div><strong>{selectedCandidate.full_name || `Кандидат #${selectedCandidate.id}`}</strong><span>Будет добавлен в выбранный слот</span></div>
                                    <i aria-hidden="true">✓</i>
                                </div>
                            )}
                        </div>
                        <div className="calendar-dialog-footer calendar-dialog-footer--split">
                            {onDeleteSlot && (
                                <button onClick={() => onDeleteSlot(slot)} className="calendar-button calendar-button--delete-quiet" disabled={loading} type="button">Удалить слот</button>
                            )}
                            <div className="calendar-dialog-actions"><button onClick={onClose} className="calendar-button calendar-button--secondary" disabled={loading}>Отмена</button><button onClick={handleBook} disabled={loading || !selectedCandidateId} className="calendar-button calendar-button--primary">{loading ? "Бронируем…" : "Забронировать"}</button></div>
                        </div>
                    </div>
                    <InterviewInviteModal
                        open={Boolean(inviteCandidate)}
                        candidate={inviteCandidate}
                        defaultDate={slot?.date || ""}
                        defaultTime={String(slot?.start_time || "").slice(0, 5)}
                        lockSchedule
                        onClose={() => {
                            afterInviteFlow();
                        }}
                        onSubmit={submitInterviewInvite}
                    />
                </div>
            ) : null}
            <InterviewReminderModal
                open={Boolean(reminderCandidate)}
                candidate={reminderCandidate}
                date={slot.date}
                startTime={String(slot.start_time || "").slice(0, 5)}
                endTime={String(slot.end_time || "").slice(0, 5)}
                onClose={() => closeAfterReminder(false)}
                onSkip={() => closeAfterReminder(true)}
                onSaved={() => closeAfterReminder(true)}
            />
        </>
    );
}
